// this is only place we should import next/config;
// rest of app should consume the typesafe getNextConfigValues
// eslint-disable-next-line no-restricted-imports

export interface NextRuntimeConfig {
  serverRuntimeConfig: {
    NODE_ENV: string;
    STRIPE_SECRET_KEY: string;
    apiUrls: {
      railsRest: string;
      railsGraph: string;
      ecommCatalogGraph: string;
    };
  };

  publicRuntimeConfig: {
    ENV: string;
    apiUrls: {
      cartOrderGraph: string;
      catalogGraph: string;
      railsRoot: string;
      // @todo deprecate; just extend from railsRest
      railsCheckoutV1: string;
      railsGraph: string;
      railsOrders: string;
    };

    frontendUrls: {
      menu: string;
      checkout: string;
      subMgmt: string;
      orderMgmt: string;
      customerDashboard: string;
      customizer: string;
      homepage: string;
    };

    segment: {
      proxy: string;
    };

    SMARTYSTREETS_CLIENT_KEY: string;
    SMARTYSTREETS_HOST: string;
    SMARTY_STREETS_ENABLED: boolean;

    HONEYBADGER_API_KEY: string;
    HONEYBADGER_ASSETS_URL: string;
    HONEYBADGER_ENV: string;
    HONEYBADGER_REVISION: string;

    STAGING_HTTP_AUTH: string | undefined;
    GOOGLE_CLIENT_ID: string;
  };
}

export const nextConfigEnvVars = {
  //NextRuntimeConfig, but with process.env.XXX as value
  serverRuntimeConfig: {
    NODE_ENV: process.env.NODE_ENV,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY!,
    apiUrls: {
      railsRest: process.env.RAILS_REST_API_URL!,
      railsGraph: process.env.RAILS_GRAPH_URL!,
      ecommCatalogGraph: process.env.ECOMM_CATALOG_INTERNAL_GRAPH!,
    },
  },
  publicRuntimeConfig: {
    ENV: process.env.NEXT_PUBLIC_ENV!,
    apiUrls: {
      cartOrderGraph:
        process.env.NEXT_PUBLIC_CART_ORDER_GRAPH_URL || `/api/v1/cart-order`,
      catalogGraph:
        process.env.NEXT_PUBLIC_CATALOG_GRAPH_URL || '/api/v1/ecomm-catalog',
      railsRoot: process.env.NEXT_PUBLIC_RAILS_REST_ROOT || '/',
      railsCheckoutV1:
        process.env.NEXT_PUBLIC_RAILS_CHECKOUT_V1_URL || '/checkout/v1',
      railsGraph: process.env.NEXT_PUBLIC_RAILS_GRAPH_URL || '/graphql',
      railsOrders: process.env.NEXT_PUBLIC_RAILS_ORDERS_API_URL || '/orders',
    },

    frontendUrls: {
      menu: '/menu',
      checkout: '/checkout',
      customerDashboard: `/dashboard`,
      customizer: '/edit-meals',
      subMgmt: '/subscription-prefs',
      orderMgmt: '/order-prefs',
      homepage: process.env.NEXT_PUBLIC_HOMEPAGE_ROUTE || '/',
    },

    segment: {
      proxy: process.env.NEXT_PUBLIC_SEGMENT_PROXY_URL || '/api/segment',
    },

    /**
     * HONEYBADGER_REVISION is used when configuring HoneyBadger to add version context to logs (typically
     * the latest git sha).
     *
     * SOURCE_VERSION and HEROKU_SLUG_COMMIT come from Heroku, during build and runtime respectively, and are equal
     * to latest commit sha, which is what we need in deployed environment.
     *
     * To override for local development, either set SOURCE_VERSION or NEXT_PUBLIC_HONEYBADGER_REVISION.
     */

    HONEYBADGER_API_KEY: process.env.NEXT_PUBLIC_HONEYBADGER_API_KEY!,
    HONEYBADGER_ASSETS_URL: process.env.NEXT_PUBLIC_HONEYBADGER_ASSETS_URL!,
    HONEYBADGER_ENV:
      process.env.NEXT_PUBLIC_HONEYBADGER_ENV! || process.env.ENV!,
    HONEYBADGER_REVISION:
      process.env.SOURCE_VERSION ||
      process.env.HEROKU_SLUG_COMMIT ||
      process.env.NEXT_PUBLIC_HONEYBADGER_REVISION ||
      '',

    // Smartystreets is used validate addresses in our system. The system will still work
    // if we don't have this enabled, but we should always validate addresses.
    SMARTYSTREETS_CLIENT_KEY: process.env.NEXT_PUBLIC_SMARTYSTREETS_CLIENT_KEY!,
    SMARTYSTREETS_HOST: process.env.NEXT_PUBLIC_SMARTYSTREETS_HOST!,
    SMARTY_STREETS_ENABLED: parseBoolean(
      process.env.NEXT_PUBLIC_SMARTY_STREETS_ENABLED!,
    ),

    // The Rails application is locked down in staging and requires an HTTP Basic Authorization header to
    // make requests. This should be set to the base64 encoded value if calling Rails stg.
    STAGING_HTTP_AUTH: process.env.NEXT_PUBLIC_STAGING_HTTP_AUTH!,
    GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!,
  },
};
/**
 * Just calls next's getConfig() but allows us to define the return type to
 * get some type safety on it.
 */
export const getNextConfigValues = (): NextRuntimeConfig => nextConfigEnvVars;

function parseBoolean(value: string) {
  return !!value && value.toLowerCase() === 'true';
}
